import 'react-tabs/style/react-tabs.css';
import 'moment/locale/pt-br';
import moment from 'moment';

import { Stack, Button, Tooltip } from '@mui/material';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

import Input from '../../form/Input';
import TextArea from '../../form/TextArea';
import InputNumber from '../../form/InputNumber';
import Select from '../../form/Select';
import DataList from '../../form/DataList';
import Checkbox from '../../form/Checkbox';
import SelectEmpresa from '../../form/SelectEmpresa';

import { retornaData } from '../../../utils/retornaDataFormatada';
import { formatarNumero } from '../../../utils/masks';

function Form({
    dados,
    parametro,
    pessoa,
    centroCusto,
    subCentroCusto,
    lancamentoBaixa,
    mapaAbastecimento,

    optionsEmpresa,
    optionsForma,
    optionsPrazos,
    optionsConta,
    contasDisponiveis,
    optionsParceiro,
    optionsCC,
    optionsMotorista,
    optionsVTR,

    divBaixaLancamento,
    divMapaAbastecimento,
    divCalculaJuros,
    readOnlyCalculaJuros,
    isSubmitting,

    btnText,
    caminhoVoltar,

    handleChange,
    handleOnBlur,
    handleOnFocus,
    submit,
}) {
    const navigate = useNavigateParams();
    const urlParams = new URLSearchParams(window.location.search);

    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const origem = urlParams.get('origem');

    const params = new URLSearchParams({
        ul,
        li,
        tipo,
        operacao: operacaoUUID,
        uuid,
        hasChanged: true,
        origem,
    });

    const isSubmitDisabled =
        isSubmitting ||
        !dados.valorOriginal ||
        !dados.centroCustoId ||
        !dados.contaId ||
        !dados.prazoId ||
        (lancamentoBaixa.baixaLancamento && (!lancamentoBaixa.baixa || Number(lancamentoBaixa.baixa) <= 0)) ||
        (btnText === 'Baixar' && (!dados.baixa || Number(dados.baixa) <= 0));

    return (
        <form onSubmit={submit}>
            <h1>Lançamentos {tipo}</h1>

            <div>
                {btnText === 'Cadastrar' && tipo === 'Pagar' && (
                    <Checkbox
                        title="Transferência de valores entre contas"
                        text="Transferência"
                        name="transferencia"
                        handleOnChange={handleChange}
                        value={dados.transferencia}
                        checked={!!dados.transferencia}
                    />
                )}

                <SelectEmpresa
                    text="Selecione a Empresa"
                    name="empresaId"
                    options={optionsEmpresa}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.empresaId ?? ''}
                    required
                    notOptNull={true}
                />

                <Input
                    text="Data"
                    type="date"
                    name="dataLancamento"
                    placeholder="Data"
                    title="Data de lançamento no sistema financeiro"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={(dados.dataLancamento && retornaData(dados.dataLancamento)) || retornaData(moment())}
                />

                <DataList
                    text="Selecione o Parceiro"
                    name="Pessoa.nomePrincipal"
                    inputWidth="600px"
                    filteredOptions={optionsParceiro}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={pessoa?.nomePrincipal || ''}
                    readonly={btnText === 'Baixar'}
                />
            </div>

            <div>
                <DataList
                    text="Centro de Custo"
                    title="Escolha o centro de custo"
                    placeholder="Digite para pesquisar"
                    name="centroCusto.descricao"
                    inputWidth="310px"
                    filteredOptions={optionsCC}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={centroCusto?.descricao || ''}
                />
                {parametro.usaSubCentroCusto === true && (
                    <DataList
                        text="SUB Centro de Custo"
                        title="Escolha o sub centro de custo"
                        placeholder="Digite para pesquisar"
                        name="subCentroCusto.descricao"
                        inputWidth="310px"
                        filteredOptions={optionsCC}
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={subCentroCusto?.descricao || ''}
                    />
                )}
                <Select
                    name="contaId"
                    title={`Define a conta para ${tipo}`}
                    text="Conta"
                    inputWidth="180px"
                    options={optionsConta}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.contaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                />

                {dados.transferencia === true && (
                    <Select
                        name="contaDestinoId"
                        title="Define a conta destino da transferência"
                        text="Conta Destino"
                        inputWidth="180px"
                        options={contasDisponiveis}
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.contaDestinoId ?? ''}
                        isEnum={false}
                        notOptNull={false}
                    />
                )}

                <Select
                    name="formaId"
                    title={`Define a forma para ${tipo}`}
                    text="Forma"
                    inputWidth="240px"
                    options={optionsForma}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.formaId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                    disabled={btnText === 'Baixar'}
                />

                <Select
                    name="prazoId"
                    title={`Define o prazo para ${tipo}`}
                    text="Prazo"
                    inputWidth="180px"
                    options={optionsPrazos}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.prazoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                    disabled={btnText === 'Baixar'}
                />
            </div>

            <div>
                <Input
                    text="Vencimento"
                    type="date"
                    name="dataVencimento"
                    placeholder="Data"
                    title="Data de vencimento da primeira parcela"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={(dados.dataVencimento && retornaData(dados.dataVencimento)) || retornaData(moment())}
                />

                <Checkbox
                    title="Mantém fixo o dia do vencimento"
                    text="Fixa Venc."
                    name="fixaDiaVencimento"
                    handleOnChange={handleChange}
                    value={dados.fixaDiaVencimento}
                    checked={!!dados.fixaDiaVencimento}
                />

                <Checkbox
                    title="Informa se o lançamento vai ficar provisionado ou não"
                    text="Provisão"
                    name="provisao"
                    handleOnChange={handleChange}
                    value={dados.provisao}
                    checked={!!dados.provisao}
                />

                <InputNumber
                    text="Valor Título"
                    type="text"
                    name="valorOriginal"
                    placeholder="Valor R$"
                    inputWidth="150px"
                    title="Digite o valor total do título"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.valorOriginal ? formatarNumero(dados.valorOriginal) : ''}
                    readonly={btnText === 'Baixar'}
                    required={btnText !== 'Baixar'}
                />

                <Input
                    text="Parcelas"
                    type="number"
                    name="parcela"
                    placeholder="parcelas"
                    inputWidth="100px"
                    title="Número de parcelas"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.parcela || ''}
                    textAlign="center"
                    min={1}
                    readonly
                />

                <InputNumber
                    text={btnText === 'Atualizar' ? `Valor da Parcela ${dados.parcelas || ''}` : 'Valor da Parcela'}
                    type="text"
                    name="valor"
                    placeholder="Valor R$"
                    inputWidth="160px"
                    title="Digite o valor da parcela"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.valor ? formatarNumero(dados.valor) : ''}
                    readonly={btnText === 'Baixar'}
                />

                <InputNumber
                    text="Valor em Aberto"
                    type="text"
                    name="valorAberto"
                    placeholder="Valor R$"
                    inputWidth="160px"
                    title="Digite o valor em aberto"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.valorAberto ? formatarNumero(dados.valorAberto) : ''}
                    readonly={btnText === 'Baixar'}
                />
            </div>

            <div>
                <InputNumber
                    text="Desconto em %"
                    type="text"
                    name="descontoPercentual"
                    placeholder="Valor Percentual"
                    inputWidth="140px"
                    title="Digite o desconto em percentual"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.descontoPercentual ? formatarNumero(dados.descontoPercentual) : ''}
                    readonly={btnText === 'Baixar'}
                />

                <InputNumber
                    text="Desconto em R$"
                    type="text"
                    name="descontoValor"
                    placeholder="Valor R$"
                    inputWidth="160px"
                    title="Digite o desconto em reais"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.descontoValor ? formatarNumero(dados.descontoValor) : ''}
                    readonly={btnText === 'Baixar'}
                />

                <InputNumber
                    text="Acréscimo em %"
                    type="text"
                    name="acrescimoPercentual"
                    placeholder="Valor percentual"
                    inputWidth="140px"
                    title="Digite o acréscimo em percentual"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.acrescimoPercentual ? formatarNumero(dados.acrescimoPercentual) : ''}
                    readonly={btnText === 'Baixar'}
                />

                <InputNumber
                    text="Acréscimo em R$"
                    type="text"
                    name="acrescimoValor"
                    placeholder="Valor R$"
                    inputWidth="160px"
                    title="Digite o acréscimo em reais"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.acrescimoValor ? formatarNumero(dados.acrescimoValor) : ''}
                    readonly={btnText === 'Baixar'}
                />

                <Checkbox
                    title="Executa cálculo automático de juros"
                    text="Calcula Juros"
                    name="calculaJuros"
                    handleOnChange={handleChange}
                    value={dados.calculaJuros}
                    checked={!!dados.calculaJuros}
                />

                {tipo.toLocaleLowerCase() === 'receber' && (
                    <>
                        <Checkbox
                            title="Informa que esse título será negociado com factoring ou banco, a liquidação real do mesmo só se dará quando o cliente efetuar o pagamento"
                            text="Negociado"
                            name="negociado"
                            handleOnChange={handleChange}
                            value={dados.negociado}
                            checked={!!dados.negociado}
                        />
                    </>
                )}

                {btnText === 'Baixar' && (
                    <>
                        <InputNumber
                            text="Valor da Baixa"
                            type="text"
                            name="baixa"
                            placeholder="Valor R$"
                            inputWidth="160px"
                            title="Digite o valor da baixa"
                            handleOnChange={handleChange}
                            handleOnFocus={handleOnFocus}
                            handleOnBlur={handleOnBlur}
                            value={dados.baixa ? formatarNumero(dados.baixa) : ''}
                        />

                        <Input
                            text="Data da Baixa"
                            type="date"
                            name="dataBaixa"
                            placeholder="Data da Baixa"
                            title="Data da baixa"
                            handleOnChange={handleChange}
                            handleOnFocus={handleOnFocus}
                            handleOnBlur={handleOnBlur}
                            value={(dados.dataBaixa && retornaData(dados.dataBaixa)) || retornaData(moment())}
                        />
                    </>
                )}

                {divBaixaLancamento && (
                    <>
                        <Checkbox
                            title="Baixa o lançamento automaticamente"
                            text="Baixa"
                            name="lancamentoBaixa.baixaLancamento"
                            handleOnChange={handleChange}
                            value={lancamentoBaixa.baixaLancamento}
                            checked={!!lancamentoBaixa.baixaLancamento}
                        />

                        <InputNumber
                            text="Valor da Baixa"
                            type="text"
                            name="lancamentoBaixa.baixa"
                            placeholder="Valor R$"
                            inputWidth="160px"
                            title="Digite o valor da baixa"
                            handleOnChange={handleChange}
                            handleOnFocus={handleOnFocus}
                            handleOnBlur={handleOnBlur}
                            value={lancamentoBaixa.baixa ? formatarNumero(lancamentoBaixa.baixa) : ''}
                        />

                        <Input
                            text="Data da Baixa"
                            type="date"
                            name="lancamentoBaixa.dataBaixa"
                            placeholder="Data da Baixa"
                            title="Data da baixa"
                            handleOnChange={handleChange}
                            handleOnFocus={handleOnFocus}
                            handleOnBlur={handleOnBlur}
                            value={(lancamentoBaixa.dataBaixa && retornaData(lancamentoBaixa.dataBaixa)) || retornaData(moment())}
                        />
                    </>
                )}
            </div>

            {divCalculaJuros && (
                <div>
                    <Input
                        text="Atraso"
                        type="number"
                        name="diasAtraso"
                        placeholder="Atraso"
                        inputWidth="100px"
                        title="Dias de atraso"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.diasAtraso || ''}
                        textAlign="center"
                        min={1}
                        readonly
                    />

                    <InputNumber
                        text="Percentual Multa"
                        type="text"
                        name="multaPercentual"
                        placeholder="Valor %"
                        inputWidth="160px"
                        title="Digite o percentual da multa"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.multaPercentual ? formatarNumero(dados.multaPercentual) : ''}
                        readonly={readOnlyCalculaJuros}
                    />

                    <InputNumber
                        text="Multa"
                        type="text"
                        name="multa"
                        placeholder="Valor R$"
                        inputWidth="160px"
                        title="Valor da multa"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.multa ? formatarNumero(dados.multa) : ''}
                        readonly={readOnlyCalculaJuros || btnText === 'Cadastrar'}
                    />

                    <InputNumber
                        text="Percentual Juros"
                        type="text"
                        name="jurosPercentual"
                        placeholder="Valor %"
                        inputWidth="160px"
                        title="Digite o percentual dos juros"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.jurosPercentual ? formatarNumero(dados.jurosPercentual) : ''}
                        readonly={readOnlyCalculaJuros}
                    />

                    <InputNumber
                        text="Juros"
                        type="text"
                        name="juros"
                        placeholder="Valor R$"
                        inputWidth="160px"
                        title="Valor dos juros"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={dados.juros ? formatarNumero(dados.juros) : ''}
                        readonly={readOnlyCalculaJuros || btnText === 'Cadastrar'}
                    />
                </div>
            )}

            {divMapaAbastecimento && (
                <div>
                    <Checkbox
                        title="Grava mapa de abastecimento"
                        text="Mapa"
                        name="mapaAbastecimento.mapa"
                        handleOnChange={handleChange}
                        value={mapaAbastecimento.mapa}
                        checked={!!mapaAbastecimento.mapa}
                    />

                    <Select
                        name="mapaAbastecimento.pessoaId"
                        title="Selecione o motorista"
                        text="Motorista"
                        inputWidth="180px"
                        options={optionsMotorista}
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.pessoaId ?? ''}
                        isEnum={false}
                        notOptNull={true}
                        required={mapaAbastecimento.mapa === true}
                    />

                    <Select
                        name="mapaAbastecimento.produtoId"
                        title="Selecione o veículo"
                        text="Veículo"
                        inputWidth="180px"
                        options={optionsVTR}
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.produtoId ?? ''}
                        isEnum={false}
                        notOptNull={true}
                        required={mapaAbastecimento.mapa === true}
                    />

                    <Input
                        text="Km Anterior"
                        type="number"
                        name="mapaAbastecimento.kmAnterior"
                        placeholder="Km"
                        inputWidth="100px"
                        title="Km anterior"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.kmAnterior || ''}
                        textAlign="center"
                        readonly
                    />

                    <Input
                        text="Km Atual"
                        type="number"
                        name="mapaAbastecimento.km"
                        placeholder="Km"
                        inputWidth="100px"
                        title="Digite o km no momento do abastecimento"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.km || ''}
                        textAlign="center"
                        required={mapaAbastecimento.mapa === true}
                    />

                    <InputNumber
                        text="Litros"
                        type="text"
                        name="mapaAbastecimento.litros"
                        placeholder="Litros"
                        inputWidth="150px"
                        title="Digite a quantidade de litros no momento do abastecimento"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.litros ? formatarNumero(mapaAbastecimento.litros) : ''}
                        required={mapaAbastecimento.mapa === true}
                    />

                    <InputNumber
                        text="Preço"
                        type="text"
                        name="mapaAbastecimento.preco"
                        placeholder="Preço R$"
                        inputWidth="150px"
                        title="Digite o valor do litro"
                        handleOnChange={handleChange}
                        handleOnFocus={handleOnFocus}
                        handleOnBlur={handleOnBlur}
                        value={mapaAbastecimento.preco ? formatarNumero(mapaAbastecimento.preco) : ''}
                        required={mapaAbastecimento.mapa === true}
                    />
                </div>
            )}
            <div>
                <Input
                    text="Documento"
                    type="text"
                    name="documento"
                    placeholder="número documento"
                    inputWidth="160px"
                    title="Digite número do documento"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.documento || ''}
                    textAlign="center"
                />

                <Input
                    text="Nosso Número"
                    type="text"
                    name="nossoNumero"
                    placeholder="Nosso Número"
                    inputWidth="160px"
                    title="Digite o nosso número"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.nossoNumero || ''}
                    textAlign="center"
                />

                <Input
                    text="Boleto"
                    type="text"
                    name="numeroBoleto"
                    placeholder="número boleto"
                    inputWidth="160px"
                    title="Digite número do boleto"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.numeroBoleto || ''}
                    textAlign="center"
                />

                <Input
                    text="Código de Barras / Chave PIX"
                    type="text"
                    name="codBarraPIX"
                    placeholder="código de barras ou chave PIX"
                    inputWidth="380px"
                    title="Digite o código de barras ou chave PIX"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.codBarraPIX || ''}
                    textAlign="center"
                    barcodeScanner
                    barcodeScannerDisabled={isSubmitting}
                />
            </div>
            <div>
                <TextArea
                    text="Histórico"
                    type="text"
                    name="historico"
                    placeholder="Digite aqui o histórico do lançamento"
                    title="Digite o histórico do lançamento"
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    handleOnBlur={handleOnBlur}
                    value={dados.historico || ''}
                    textAlign="left"
                    linhas="4"
                    colunas="120"
                />
            </div>

            <div>
                <Stack direction="row" spacing={1}>
                    <Tooltip arrow placement="top-start" title="Voltar">
                        <Button variant="contained" color="secondary" onClick={() => navigate(caminhoVoltar, params)}>
                            Voltar
                        </Button>
                    </Tooltip>

                    <Button type="submit" variant="contained" color="success" disabled={isSubmitDisabled}>
                        {btnText}
                    </Button>
                </Stack>
            </div>
        </form>
    );
}

export default Form;
